import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import s from "../modules/DescEdit.module.css";

/**
 * Редактирование отдельного описания (tushuncha)
 * GET   /api/tushunchalar/{id}/  -> { id, vazifa, matni, ... }
 * PATCH /api/tushunchalar/{id}/  -> { matni }
 * Для заголовка подтягиваем тренажёр: GET /api/vazifalar/{vazifa}/
 * CKEditor 4 грузим через CDN и инициализируем на textarea по id
 */

const TUSHUNCHALAR_ENDPOINT =
  process.env.REACT_APP_TUSHUNCHALAR_ENDPOINT ||
  "https://apitr.subxonqulov.uz/api/tushunchalar/";

const VAZIFALAR_ENDPOINT =
  process.env.REACT_APP_VAZIFALAR_ENDPOINT ||
  "https://apitr.subxonqulov.uz/api/vazifalar/";

// ===== helpers: auth
function getAnyTokenFromStorage() {
  const pick = (s) =>
    s.getItem("auth:access") || s.getItem("auth:token") || s.getItem("auth:key") ||
    s.getItem("auth:jwt") || s.getItem("auth:auth_token") || null;
  return pick(window.localStorage) || pick(window.sessionStorage);
}
function makeAuthHeader(tokenLike) {
  if (!tokenLike) return {};
  const token = String(tokenLike);
  const forcedScheme = process.env.REACT_APP_AUTH_SCHEME; // "Bearer" | "Token" | "JWT"
  if (forcedScheme) return { Authorization: `${forcedScheme} ${token}` };
  const looksLikeJwt = token.startsWith("eyJ");
  return looksLikeJwt ? { Authorization: `Bearer ${token}` } : { Authorization: `Token ${token}` };
}

export default function DescEdit() {
  const { id } = useParams(); // id описания
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [vazifaId, setVazifaId] = useState(null);
  const [vazifaName, setVazifaName] = useState("");
  const [html, setHtml] = useState("");

  // CKEditor
  const editorRef = useRef(null);
  const editorId = useMemo(() => "desc-editor-" + Math.random().toString(36).slice(2), []);

  function ensureCKEScript() {
    return new Promise((resolve, reject) => {
      if (window.CKEDITOR) return resolve();
      const script = document.createElement("script");
      script.src = "https://cdn.ckeditor.com/4.22.1/full-all/ckeditor.js";
      script.onload = () => resolve();
      script.onerror = reject;
      document.body.appendChild(script);
    });
  }

  const commonCkeConfig = {
    language: "ru",
    height: 320,
    contentsLangDirection: "ltr",
    contentsCss: [
      "https://cdn.ckeditor.com/4.22.1/full-all/contents.css",
      "body { direction: ltr; text-align: left; }",
    ],
    toolbar: [
      { name: "document", items: ["Source","-","Preview","Print"] },
      { name: "clipboard", items: ["Cut","Copy","Paste","PasteText","PasteFromWord","-","Undo","Redo"] },
      { name: "editing",  items: ["Find","Replace","SelectAll","Scayt"] },
      "/",
      { name: "basicstyles", items: ["Bold","Italic","Underline","Strike","RemoveFormat"] },
      { name: "paragraph",   items: ["NumberedList","BulletedList","-","Outdent","Indent","-","Blockquote","-","JustifyLeft","JustifyCenter","JustifyRight","JustifyBlock","-","BidiLtr","BidiRtl"] },
      { name: "links",       items: ["Link","Unlink","Anchor"] },
      { name: "insert",      items: ["Image","Table","HorizontalRule","Smiley","SpecialChar","Iframe"] },
      "/",
      { name: "styles", items: ["Styles","Format","Font","FontSize"] },
      { name: "colors", items: ["TextColor","BGColor"] },
      { name: "tools",  items: ["Maximize","ShowBlocks"] },
    ],
    extraPlugins: "colorbutton,font,justify,language,bidi,div,find,smiley,iframe",
    removePlugins: "elementspath,notification",
    resize_enabled: true,
  };

  // Load description and trainer info
  useEffect(() => {
    let abort = false;
    (async () => {
      setLoading(true); setError("");
      try {
        const token = getAnyTokenFromStorage();
        const res = await fetch(`${TUSHUNCHALAR_ENDPOINT}${id}/`, {
          headers: { "Content-Type": "application/json", ...(token ? makeAuthHeader(token) : {}) },
          credentials: "include",
        });
        const data = await res.json().catch(() => null);
        if (!res.ok) throw new Error(data?.detail || data?.message || "Tavsifni yuklashda xatolik yuz berdi");
        if (abort) return;
        setHtml(data?.matni || "");
        setVazifaId(data?.vazifa || null);

        // подтянем название тренажёра
        if (data?.vazifa) {
          const vRes = await fetch(`${VAZIFALAR_ENDPOINT}${data.vazifa}/`, { credentials: "include" });
          const vData = await vRes.json().catch(() => null);
          if (!abort) setVazifaName(vData?.name || "");
        }

        // инициализируем CKEditor после загрузки данных
        await ensureCKEScript();
        if (abort) return;
        if (window.CKEDITOR.instances[editorId]) {
          window.CKEDITOR.instances[editorId].destroy(true);
        }
        const inst = window.CKEDITOR.replace(editorId, commonCkeConfig);
        editorRef.current = inst;
        inst.setData(data?.matni || "");
        inst.on("change", () => setHtml(inst.getData()));
        inst.on("instanceReady", () => { try { inst.resize('100%', 320); } catch {} });
      } catch (e) {
        if (!abort) setError(e?.message || "Ошибка загрузки");
      } finally {
        if (!abort) setLoading(false);
      }
    })();

    return () => {
      abort = true;
      if (window.CKEDITOR && window.CKEDITOR.instances[editorId]) {
        window.CKEDITOR.instances[editorId].destroy(true);
      }
    };
  }, [id, editorId]);

  async function handleSave(e) {
    e.preventDefault();
    setError(""); setSuccess("");
    try {
      setSaving(true);
      const token = getAnyTokenFromStorage();
      const payload = { matni: html || "" };
      const res = await fetch(`${TUSHUNCHALAR_ENDPOINT}${id}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...(token ? makeAuthHeader(token) : {}) },
        body: JSON.stringify(payload),
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const serverMsg = (typeof data === "string" && data) || data?.detail || data?.error || data?.message ||
          (data && Array.isArray(Object.values(data)[0]) ? Object.values(data)[0] : null) || "Не удалось сохранить";
        throw new Error(Array.isArray(serverMsg) ? serverMsg.join(" ") : String(serverMsg));
      }
      setSuccess("Сохранено");
    } catch (e) {
      setError(e?.message || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm("Удалить это описание? Действие необратимо.")) return;
    try {
      const token = getAnyTokenFromStorage();
      const res = await fetch(`${TUSHUNCHALAR_ENDPOINT}${id}/`, {
        method: "DELETE",
        headers: { ...(token ? makeAuthHeader(token) : {}) },
        credentials: "include",
      });
      if (!res.ok && res.status !== 204) throw new Error("Сервер отклонил удаление");
      navigate("/trainer/descriptions", { replace: true });
    } catch (e) {
      setError(e?.message || "Не удалось удалить");
    }
  }

  if (loading) return <div className={s.wrapper}><div className={s.info}>Загрузка…</div></div>;

  return (
    <div className={s.wrapper}>
      <div className={s.headRow}>
        <h1 className={s.title}>Редактирование описания</h1>
        <div className={s.actions}>
          <Link to="/trainer/descriptions" className={s.secondary}>← К списку</Link>
          <button className={s.danger} onClick={handleDelete}>Удалить</button>
        </div>
      </div>

      {vazifaId && (
        <div className={s.meta}>Тренажёр: {vazifaName ? <Link to={`/trainer/${vazifaId}`}>{vazifaName}</Link> : `#${vazifaId}`}</div>
      )}

      {error && <div className={s.error}>{error}</div>}
      {success && <div className={s.success}>{success}</div>}

      <form className={s.form} onSubmit={handleSave}>
        <div className={s.field}>
          <label className={s.label}>Текст описания</label>
          {/* На этот textarea навесится CKEditor */}
          <textarea id={editorId} defaultValue={html} />
          <div className={s.hint}>Изменения сохраняются в поле <code>matni</code> этой записи.</div>
        </div>
        <div className={s.actions}>
          <button className={s.submit} type="submit" disabled={saving}>
            {saving ? "Сохраняем…" : "Сохранить"}
          </button>
          <button type="button" className={s.secondary} onClick={() => navigate(-1)} disabled={saving}>
            Отмена
          </button>
        </div>
      </form>
    </div>
  );
}


